import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Truck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTAClick } from "@/lib/ga4";
import productBottle from "@/assets/product-bottles-capsules.png";

const OptimizedHeroSection = () => {
  const scrollToPricing = () => {
    trackCTAClick("GET MY NAD+ NOW", "hero_section");
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToScience = () => {
    document.getElementById("science")?.scrollIntoView({ behavior: "smooth" });
  };

  // Countdown timer — resets every 24h
  const getTimeLeft = () => {
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    const diff = endOfDay.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { hours, minutes, seconds };
  };

  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n: number) => n.toString().padStart(2, "0");

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background pt-20 pb-12 md:py-0">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/8 rounded-full blur-[120px] animate-pulse-glow" style={{ animationDelay: "1.5s" }} />
        <div className="absolute inset-0 bg-[linear-gradient(hsl(var(--primary)/0.03)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--primary)/0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="container relative z-10 px-4 mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center lg:text-left order-2 lg:order-1"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6"
            >
              <span className="text-primary text-sm font-bold tracking-wide">★ FORMULATED BY LONGEVITY SCIENTISTS</span>
            </motion.div>

            {/* Headline */}
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-[58px] font-bold leading-[1.08] mb-5 tracking-tight">
              <span className="text-foreground">Your Body Is 45.</span>
              <br />
              <span className="text-foreground">Your Cells Can Feel </span>
              <span className="text-gradient-gold">30 Again.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl mx-auto lg:mx-0">
              NAD⁺ is the molecule scientists call the <span className="text-foreground font-semibold">'master regulator of aging'</span> — and after 40, your levels drop by 50%. REVIONX replenishes it at the cellular level.
            </p>

            {/* Urgency countdown */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-6 p-3 rounded-xl bg-destructive/10 border border-destructive/30 inline-flex items-center gap-3"
            >
              <span className="text-destructive text-sm md:text-base font-bold">⏰ LIMITED OFFER: 20% OFF ends in</span>
              <span className="font-mono text-foreground font-bold text-base md:text-lg bg-card px-3 py-1 rounded-lg border border-border">
                {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
              </span>
            </motion.div>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-5">
              <Button
                size="lg"
                onClick={scrollToPricing}
                className="group h-14 md:h-16 px-10 md:px-14 text-base md:text-lg font-extrabold gradient-cta text-primary-foreground hover:opacity-90 shadow-cta transition-all duration-300 hover:shadow-[0_12px_50px_hsl(43_82%_46%/0.5)] animate-pulse-scale btn-premium rounded-xl md:rounded-2xl tracking-wide"
              >
                <span>GET MY NAD+ NOW — SAVE 20%</span>
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </Button>
            </div>

            {/* Secondary CTA */}
            <div className="mb-6">
              <button
                onClick={scrollToScience}
                className="text-muted-foreground hover:text-primary text-sm font-medium transition-colors underline underline-offset-4 decoration-muted-foreground/30 hover:decoration-primary"
              >
                See How It Works ↓
              </button>
            </div>

            {/* Trust bar */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Lock className="w-4 h-4 text-primary" />
                <span>60-Day Money-Back Guarantee</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Truck className="w-4 h-4 text-primary" />
                <span>Free Shipping Over $50</span>
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>Made in USA · GMP Certified</span>
              </div>
            </div>

            {/* Lab tested badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-primary/20"
            >
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span className="text-foreground text-xs md:text-sm font-medium">Third-Party Tested & Verified</span>
            </motion.div>
          </motion.div>

          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative order-1 lg:order-2 flex justify-center"
          >
            {/* Glow */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 md:w-80 md:h-80 bg-primary/20 rounded-full blur-[100px] animate-pulse-glow" />
            </div>

            {/* Rotating ring */}
            <motion.div
              className="absolute w-72 h-72 md:w-96 md:h-96 border border-primary/15 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            >
              <div className="absolute top-0 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 bg-primary rounded-full" />
              <div className="absolute bottom-0 left-1/2 w-2 h-2 -translate-x-1/2 translate-y-1/2 bg-accent rounded-full" />
            </motion.div>

            {/* Product bottle */}
            <motion.img
              src={productBottle}
              alt="REVIONX NAD+ Premium Supplement"
              className="relative z-10 w-52 md:w-72 lg:w-80 drop-shadow-2xl animate-float"
              style={{ filter: "drop-shadow(0 0 50px hsl(43 82% 46% / 0.35))" }}
            />
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary/40 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-1.5 bg-primary rounded-full" />
        </motion.div>
      </motion.div>

      {/* FDA Disclaimer */}
      <div className="absolute bottom-2 left-0 right-0 text-center px-4">
        <p className="text-muted-foreground text-[10px] md:text-xs max-w-md mx-auto">
          *These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.
        </p>
      </div>
    </section>
  );
};

export default OptimizedHeroSection;
